import React, { createContext, useCallback, useContext, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import { ErrorOutline as ErrorIcon } from "@mui/icons-material";

type ErrorDisplayContextValue = {
  showError: (message: string) => void;
};

const ErrorDisplayContext = createContext<ErrorDisplayContextValue | null>(null);

export function ErrorDisplayProvider({ children }: { children: React.ReactNode }) {
  const [message, setMessage] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const showError = useCallback((msg: string) => {
    setMessage(msg);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setMessage(null);
  }, []);

  return (
    <ErrorDisplayContext.Provider value={{ showError }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={8000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          icon={<ErrorIcon />}
          onClose={handleClose}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: 2,
            "& .MuiAlert-message": { fontWeight: 500 },
          }}
        >
          {message}
        </Alert>
      </Snackbar>
    </ErrorDisplayContext.Provider>
  );
}

export function useErrorDisplay(): ErrorDisplayContextValue {
  const ctx = useContext(ErrorDisplayContext);
  if (!ctx) {
    throw new Error("useErrorDisplay must be used within ErrorDisplayProvider");
  }
  return ctx;
}
